import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import Button, { TYPES } from '../Button/index'
import { ICON_TYPES } from '../Icon.v2/index'

/**
 * The video player aspect ratio container
 * @param {Object} props - The props
 * @param {string} props.poster - The image used as a placeholder
 * @param {string} props.className - The additional class name
 * @param {number} props.aspectRatio - The aspect ratio of container
 * @param {boolean} props.isPlayerActive - The value indicating if video is playing or pause
 * @param {number} props.scrollOffsetRatio - The number between 0 and 1
 * @returns {JSX.Element} VideoPlayerContainer
 */

function VideoPlayerContainer (props) {
  const {
    poster,
    className = '',
    aspectRatio = 1.7778,
    scrollOffsetRatio = 1,
    isPlayerActive,
    children,
  } = props

  const containerRef = useRef()
  const frameRef = useRef()

  const [minimizePlayer, setMinimizePlayer] = useState(false)
  const [scrollOffset, setScrollOffset] = useState(0)
  const [stickyOffset, setStickyOffset] = useState(0)
  const [shouldMinimize, setShouldMinimize] = useState(true)
  const [stickPlayer, setStickPlayer] = useState(false)

  const containerClassName = [
    'video-player-container',
    className,
  ].filter((cls) => !!cls).join(' ')

  const holderClassName = [
    'video-player-container__holder',
    minimizePlayer && isPlayerActive ? 'video-player-container__holder--minimized' : null,
    stickPlayer && isPlayerActive ? 'video-player-container__holder--sticky' : null,
  ].filter((cls) => !!cls).join(' ')

  const posterClassName = [
    'video-player-container__poster',
    minimizePlayer && isPlayerActive ? null : 'video-player-container__poster--hidden',
  ].filter((cls) => !!cls).join(' ')

  const setFrameDimensions = useCallback(() => {
    if (!containerRef.current || !frameRef.current) {
      return
    }

    global.requestAnimationFrame(() => {
      const container = containerRef.current
      const frame = frameRef.current

      if (!container || !frame) {
        return
      }

      const containerWidth = container.clientWidth || global.innerWidth
      const containerHeight = container.clientHeight
      const containerOffsetTop = container.offsetTop
      let frameWidth = containerWidth
      let frameHeigth = containerWidth / aspectRatio
      if (containerHeight && containerHeight < frameHeigth) {
        frameHeigth = containerHeight
        frameWidth = containerHeight * aspectRatio
      }
      if (frameHeigth > global.innerHeight) {
        frameHeigth = global.innerHeight
        frameWidth = global.innerHeight * aspectRatio
      }
      frame.style.height = `${frameHeigth}px`
      frame.style.width = `${frameWidth}px`
      setStickyOffset(containerOffsetTop)
      setScrollOffset(frameHeigth * scrollOffsetRatio + containerOffsetTop)
    })
  }, [aspectRatio, scrollOffsetRatio])

  const handleVideoPositionOnScroll = useCallback(() => {
    global.requestAnimationFrame(() => {
      const { scrollY } = global
      if (shouldMinimize) {
        setMinimizePlayer(scrollY >= scrollOffset)
      }
      setStickPlayer(scrollY >= stickyOffset)
    })
  }, [scrollOffset, shouldMinimize])

  const handleClose = () => {
    setShouldMinimize(false)
    setMinimizePlayer(false)
  }

  useEffect(() => {
    setFrameDimensions()
    global.addEventListener('resize', setFrameDimensions)
    return () => {
      global.removeEventListener('resize', setFrameDimensions)
    }
  }, [setFrameDimensions])

  useEffect(() => {
    global.addEventListener('scroll', handleVideoPositionOnScroll)
    return () => {
      global.removeEventListener('scroll', handleVideoPositionOnScroll)
    }
  }, [handleVideoPositionOnScroll])

  return (
    <div ref={containerRef} className={containerClassName}>
      <div ref={frameRef} className="video-player-container__frame">
        <div className={posterClassName} style={poster ? { backgroundImage: `url(${poster}}` } : null} />
        <div className={holderClassName}>
          <Button
            type={TYPES.ICON}
            icon={ICON_TYPES.CLOSE}
            onClick={handleClose}
            className="video-player-container__close"
          />
          {children}
        </div>
      </div>
    </div>
  )
}

export default VideoPlayerContainer
